import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Card, Table, Button, Form, Badge, Dropdown } from "react-bootstrap";
import { FiDownload, FiFileText, FiCalendar, FiDollarSign, FiShoppingCart, FiTrendingUp, FiFilter } from "react-icons/fi";
import { GRADIENTS } from "utils/theme";
import Useaxios from "utils/Useaxios";
import { API } from "utils/Apiroutes";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import TableFilter from "components/ui/TableFilter";
import usePagination from "hooks/usePagination";
import Pagination from "components/ui/Pagination";
import { convertPrice } from "utils/currency";
import { getGlobalCurrency } from "utils/currency";
import { priceConversion } from "utils/currency";
import { setExchangeRate } from "utils/currency";

function SalesReport() {
  const { fetchData } = Useaxios();
   const [selectedCurrency, setSelectedCurrency] = useState(getGlobalCurrency());
 
  const fetchTransactions = useCallback(async (params) => {
    const res = await fetchData({
      url: API.getTransactions,
      method: "GET",
      params: {
        ...params,
        status: "SUCCESSFUL",
        fromDate: params.fromDate,
        toDate: params.toDate
      },
      showMessage: false
    });
    return res;
  }, []);

  const {
    data: salesData,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    filters: paginationFilters,
    goToPage,
    nextPage,
    prevPage,
    changePageSize,
    updateFilters: updatePaginationFilters,
    clearFilters: clearPaginationFilters,
    refresh
  } = usePagination(fetchTransactions, { 
    search: '',
    fromDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split("T")[0],
    toDate: new Date().toISOString().split("T")[0]
  });

  

  const [allSalesData, setAllSalesData] = useState([]);
  const [allStats, setAllStats] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    avgOrderValue: 0,
    totalSales: 0
  });

  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
    status: "all"
  });

  const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
      const handleCurrencyChange = () => {
        setSelectedCurrency(getGlobalCurrency());
      };
  
      window.addEventListener("currencyChanged", handleCurrencyChange);
  
      return () => {
        window.removeEventListener("currencyChanged", handleCurrencyChange);
      };
    }, []);

  // Fetch all stats once
  const fetchAllStats = async () => {
    try {
      const res = await fetchData({
        url: API.getTransactions,
        method: "GET",
        params: {
          page: 1,
          limit: 9999,
          status: "SUCCESSFUL",
          fromDate: paginationFilters.fromDate,
          toDate: paginationFilters.toDate
        },
        showMessage: false
      });

      if (res?.success) {
        const allTransactions = res?.data?.data || [];
        setAllSalesData(allTransactions);
        const totalRevenue = allTransactions.reduce(
          (sum, item) => sum + Number(item.amount || item.totalAmount || 0),
          0
        );
        const totalOrders = allTransactions.length;

        setAllStats({
          totalRevenue,
          totalOrders,
          totalSales: totalOrders,
          avgOrderValue: totalOrders ? Math.round(totalRevenue / totalOrders) : 0
        });
      }
    } catch (error) {
      console.error("Stats fetch error", error);
    }
  };

  useEffect(() => {
    fetchAllStats();
  }, [paginationFilters]);

  useEffect(() => {
    refresh();
  }, [paginationFilters]);

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Sales Report", 14, 20);
    doc.setFontSize(11);
    doc.text(`Period: ${filters.startDate} to ${filters.endDate}`, 14, 30);
    doc.text(`Total Revenue (LRD): ${priceConversion(allStats.totalRevenue,'LRD', 'LRD')}`, 14, 36);
    doc.text(`Total Revenue (USD): ${priceConversion(allStats.totalRevenue,'USD', 'LRD').toFixed(2)}`, 100, 36);
    doc.text(`Total Orders: ${allStats.totalOrders}`, 14, 42);

    const rows = allSalesData.map((sale, index) => [
      index + 1,
      sale.transactionId?.substring(0, 8) || "N/A",
      sale.items?.map(item => item.productId?.name).filter(Boolean).join(", ") || "N/A",
      sale.machineId || "N/A",
      priceConversion(sale.amount || sale.totalAmount || 0, 'LRD', 'LRD')?.toFixed(2) || 0,
      priceConversion(sale.amount || sale.totalAmount || 0, 'USD', 'LRD')?.toFixed(2) || 0,
      sale.status,
      new Date(sale.createdAt).toLocaleDateString()
    ]);


    autoTable(doc, {
      startY: 50,
      head: [["#", "Transaction", "Product", "Machine", "Amount (LRD)", "Amount (USD)", "Status", "Date"]],
      body: rows
    });

    doc.save(`sales-report-${filters.startDate}-to-${filters.endDate}.pdf`);
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      allSalesData.map((sale, index) => ({
        No: index + 1,
        Transaction: sale.transactionId?.substring(0, 8) || "N/A",
        Product: sale.items?.map(item => item.productId?.name).filter(Boolean).join(", ") || "N/A",
        Machine: sale.machineId || "N/A",
        "Amount (LRD)": priceConversion(sale.amount || sale.totalAmount || 0, 'LRD', 'LRD')?.toFixed(2) || 0,
        "Amount (USD)": priceConversion(sale.amount || sale.totalAmount || 0, 'USD', 'LRD')?.toFixed(2) || 0,
        Status: sale.status,
        Date: new Date(sale.createdAt).toLocaleDateString(),
        PaymentMethod: sale?.paymentMethod
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sales");
    XLSX.writeFile(workbook, `sales-report-${filters.startDate}-to-${filters.endDate}.xlsx`);
  };

  const exportToCSV = () => {
    // console.log(allSalesData)
    // return 
    const worksheet = XLSX.utils.json_to_sheet(allSalesData.map((sale, index) => ({
        No: index + 1,
        Transaction: sale.transactionId?.substring(0, 8) || "N/A",
        Product: sale.items?.map(item => item.productId?.name).filter(Boolean).join(", ") || "N/A",
        Machine: sale.machineId || "N/A",
        "Amount (LRD)": priceConversion(sale.amount || sale.totalAmount || 0, 'LRD', 'LRD')?.toFixed(2) || 0,
        "Amount (USD)": priceConversion(sale.amount || sale.totalAmount || 0, 'USD', 'LRD')?.toFixed(2) || 0,
        Status: sale.status,
        Date: new Date(sale.createdAt).toLocaleDateString(),
        PaymentMethod: sale?.paymentMethod,
      })));
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sales-report-${filters.startDate}-to-${filters.endDate}.csv`;
    a.click();
  };
  
  const filterFields = [
    {
      key: 'search',
      label: 'Search',
      type: 'text',
      placeholder: 'Search transactions...'
    },
    {
      key: 'fromDate',
      label: 'Start Date',
      type: 'date'
    },
    {
      key: 'toDate', 
      label: 'End Date',
      type: 'date'
    }
  ];

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <Card className="shadow border-0 rounded-3 overflow-hidden">
            <div style={{ background: GRADIENTS.primary }} className="text-white py-3 px-4">
              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <div>
                  <h4 className="mb-0 fw-semibold">
                    <FiFileText className="me-2" />
                    Sales Report
                  </h4>
                  <small className="opacity-75">
                    View and export sales data
                  </small>
                </div>
                <div className="d-flex gap-2">
                  <Button
                    variant="light"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <FiFilter className="me-1" />
                    Filters
                  </Button>
                  <Dropdown>
                    <Dropdown.Toggle variant="light" size="sm">
                      <FiDownload className="me-1" />
                      Export
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={exportToPDF}>
                        Export PDF
                      </Dropdown.Item>
                      <Dropdown.Item onClick={exportToExcel}>
                        Export Excel
                      </Dropdown.Item>
                      <Dropdown.Item onClick={exportToCSV}>
                        Export CSV
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
            </div>

            <Card.Body>

              {showFilters && (
                <div className="px-3 pt-3 border-bottom">
                  <TableFilter
                    filterConfig={filterFields}
                    filters={paginationFilters}
                    onFilterChange={updatePaginationFilters}
                    onClearFilters={clearPaginationFilters}
                  />
                </div>
              )}


              <Row className="mb-4">
                <Col md={3}>
                  <Card className="border-0 shadow-sm">
                    <Card.Body className="d-flex align-items-center">
                      <FiDollarSign size={22} className="me-3 text-primary" />
                      <div>
                        <small className="text-muted">Total Revenue</small>
                        <h5 className="mb-0 fw-bold">
                          {convertPrice(allStats.totalRevenue ,'LRD')?.toFixed(2)}
                        </h5>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                {/* <Col md={3}>
                  <Card className="border-0 shadow-sm">
                    <Card.Body className="d-flex align-items-center">
                      <FiShoppingCart size={22} className="me-3 text-success" />
                      <div>
                        <small className="text-muted">Total Orders</small>
                        <h5 className="mb-0 fw-bold">
                          {allStats.totalOrders}
                        </h5>
                      </div>
                    </Card.Body>
                  </Card>
                </Col> */}
                {/* <Col md={3}>
                  <Card className="border-0 shadow-sm">
                    <Card.Body className="d-flex align-items-center">
                      <FiTrendingUp size={22} className="me-3 text-info" />
                      <div>
                        <small className="text-muted">Avg Order</small>
                        <h5 className="mb-0 fw-bold">
                          {allStats.avgOrderValue}
                        </h5>
                      </div>
                    </Card.Body>
                  </Card>
                </Col> */}
                <Col md={3}>
                  <Card className="border-0 shadow-sm">
                    <Card.Body className="d-flex align-items-center">
                      <FiFileText size={22} className="me-3 text-warning" />
                      <div>
                        <small className="text-muted">Total Sales</small>
                        <h5 className="mb-0 fw-bold">
                          {allStats.totalSales}
                        </h5>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <div className="table-responsive">
                <Table hover className="mb-0 align-middle">
                  <thead className="bg-light">
                    <tr>
                      <th className="border-0">#</th>
                      <th className="border-0">Transaction</th>
                      <th className="border-0">Product</th>
                      <th className="border-0">Machine</th>
                      <th className="border-0">Amount</th>
                      <th className="border-0 text-center">Status</th>
                      <th className="border-0">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="7" className="text-center py-5">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p className="mt-3 text-muted">Loading sales data...</p>
                        </td>
                      </tr>
                    ) : salesData?.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-5">
                          <div className="display-1 text-muted mb-3">
                            <FiFileText />
                          </div>
                          <h5 className="text-muted mb-3">No Sales Data Found</h5>
                          <p className="text-muted">Try adjusting your date range or filters</p>
                        </td>
                      </tr>
                    ) : (
                      salesData.map((sale, index) => (
                        <tr key={sale._id || index} className="align-middle">
                          <td>{(currentPage - 1) * pageSize + index + 1}</td>
                          <td>
                            <div className="fw-semibold">{sale.transactionId || "N/A"}</div>
                            {/* <small className="text-muted">ID: {sale._id?.substring(0, 8)}...</small> */}
                          </td>
                          <td>{sale.items?.map(item => item.productId?.name).filter(Boolean).join(", ") || "N/A"}</td>
                          <td>
                            <Badge bg="light" text="dark" className="border">
                              <i className="fas fa-robot me-1 text-success"></i>
                              {sale.machineId || "N/A"}
                            </Badge>
                          </td>
                          <td>
                            <span className="fw-bold text-success">
                             {getGlobalCurrency()} {convertPrice(sale.amount || sale.totalAmount || 0 ,"LRD" )?.toFixed(2)}
                            </span>
                          </td>
                          <td className="text-center">
                            <Badge
                              bg={
                                sale.status?.toUpperCase() === "SUCCESSFUL" || sale.status?.toUpperCase() === "SUCCESS"
                                  ? "success"
                                  : sale.status?.toUpperCase() === "PENDING"
                                    ? "warning"
                                    : "danger"
                              }
                              className="px-3 py-1"
                            >
                              {sale.status}
                            </Badge>
                          </td>
                          <td>
                            <div className="fw-medium">
                              {new Date(sale.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </div>
                            <small className="text-muted">
                              {new Date(sale.createdAt).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </small>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {salesData?.length > 0 && (
                <Card.Footer className="bg-light border-0 py-3">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    pageSize={pageSize}
                    onPageChange={goToPage}
                    onNextPage={nextPage}
                    onPrevPage={prevPage}
                    onPageSizeChange={changePageSize}
                  />
                </Card.Footer>
              )}

            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default SalesReport;
